import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useAuth } from './AuthContext';
import OpportunitySidebar from './OpportunitySidebar';
import './css/PipelineBoard.css';
import './css/BaobabDesignSystem.css';

const PipelineBoard = () => {
  const [pipelineData, setPipelineData] = useState({
    stages: {
      prospection: [],
      qualification: [],
      proposition: [],
      négociation: [],
      gagnée: [],
      perdue: []
    },
    total_opportunities: 0
  });
  
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [filters, setFilters] = useState({
    owner: '',
    statut: '',
    amountMin: '',
    amountMax: '',
    expectedCloseDateFrom: '',
    expectedCloseDateTo: '',
    company: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableCommercials, setAvailableCommercials] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  
  const { user } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const stageConfig = {
    prospection: {
      title: 'Prospect',
      color: '#6B7280',
      bgColor: '#F9FAFB',
      icon: '🔍'
    },
    qualification: {
      title: 'Qualification',
      color: '#3B82F6',
      bgColor: '#EFF6FF',
      icon: '📋'
    },
    proposition: {
      title: 'Proposition',
      color: '#F59E0B',
      bgColor: '#FFFBEB',
      icon: '💼'
    },
    négociation: {
      title: 'Négociation',
      color: '#EF4444',
      bgColor: '#FEF2F2',
      icon: '🤝'
    },
    gagnée: {
      title: 'Gagné',
      color: '#22C55E',
      bgColor: '#F0FDF4',
      icon: '🎉'
    },
    perdue: {
      title: 'Perdu',
      color: '#6B7280',
      bgColor: '#F9FAFB',
      icon: '❌'
    }
  };

  useEffect(() => {
    fetchPipelineData();
    fetchFiltersData();
  }, [filters]);

  const fetchFiltersData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Récupérer la liste des utilisateurs pour les commerciaux
      const usersResponse = await fetch(`${API_BASE_URL}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (usersResponse.ok) {
        const users = await usersResponse.json();
        // Filtrer les commerciaux (admin, manager, commercial)
        const commercials = users.filter(user => 
          ['admin', 'manager', 'commercial'].includes(user.role)
        );
        setAvailableCommercials(commercials);
      }

      // Récupérer la liste des sociétés
      const companiesResponse = await fetch(`${API_BASE_URL}/api/crm/clients`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (companiesResponse.ok) {
        const companies = await companiesResponse.json();
        setAvailableCompanies(companies);
      }
      
    } catch (error) {
      console.error('Error fetching filters data:', error);
    }
  };

  const fetchPipelineData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/pipeline`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Appliquer les filtres côté client
        const filteredStages = {};
        Object.keys(data.stages).forEach(stage => {
          filteredStages[stage] = data.stages[stage].filter(opportunity => {
            // Filtre par commercial
            if (filters.owner && !opportunity.commercial_responsable.toLowerCase().includes(filters.owner.toLowerCase())) {
              return false;
            }
            
            // Filtre par statut
            if (filters.statut && opportunity.statut !== filters.statut) {
              return false;
            }
            
            // Filtre par société
            if (filters.company && !opportunity.client_name.toLowerCase().includes(filters.company.toLowerCase())) {
              return false;
            }
            
            // Filtre par montant minimum
            if (filters.amountMin && opportunity.valeur_estimee < parseFloat(filters.amountMin)) {
              return false;
            }
            
            // Filtre par montant maximum
            if (filters.amountMax && opportunity.valeur_estimee > parseFloat(filters.amountMax)) {
              return false;
            }
            
            // Filtre par date de clôture prévue (depuis)
            if (filters.expectedCloseDateFrom && opportunity.date_cloture_prevue) {
              const oppDate = new Date(opportunity.date_cloture_prevue);
              const fromDate = new Date(filters.expectedCloseDateFrom);
              if (oppDate < fromDate) {
                return false;
              }
            }
            
            // Filtre par date de clôture prévue (jusqu'à)
            if (filters.expectedCloseDateTo && opportunity.date_cloture_prevue) {
              const oppDate = new Date(opportunity.date_cloture_prevue);
              const toDate = new Date(filters.expectedCloseDateTo);
              if (oppDate > toDate) {
                return false;
              }
            }
            
            return true;
          });
        });
        
        setPipelineData({
          stages: filteredStages,
          total_opportunities: Object.values(filteredStages).flat().length,
          last_updated: data.last_updated
        });
      } else {
        setError('Erreur lors du chargement des données pipeline');
      }
    } catch (error) {
      console.error('Error fetching pipeline data:', error);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    
    console.log('Drag ended:', { destination, source, draggableId });
    
    // Si pas de destination ou même position, ne rien faire
    if (!destination || 
        (destination.droppableId === source.droppableId && 
         destination.index === source.index)) {
      console.log('No destination or same position');
      return;
    }
    
    const newStage = destination.droppableId;
    const opportunityId = draggableId;
    
    console.log('Moving opportunity', opportunityId, 'to stage', newStage);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/pipeline/${opportunityId}/stage`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          statut: newStage,
          lost_reason: newStage === 'perdue' ? 'Déplacé via pipeline' : null
        })
      });
      
      console.log('API Response status:', response.status);
      
      if (response.ok) {
        console.log('API call successful, updating local state');
        
        // Créer une copie profonde pour éviter les mutations
        const updatedPipelineData = JSON.parse(JSON.stringify(pipelineData));
        const sourceStage = updatedPipelineData.stages[source.droppableId];
        const destStage = updatedPipelineData.stages[destination.droppableId];
        
        // Vérifier que les stages existent
        if (!sourceStage || !destStage) {
          console.error('Source or destination stage not found');
          return;
        }
        
        // Trouver l'opportunité dans la source
        const opportunityIndex = sourceStage.findIndex(opp => opp.id === opportunityId);
        if (opportunityIndex === -1) {
          console.error('Opportunity not found in source stage');
          return;
        }
        
        const [movedOpportunity] = sourceStage.splice(opportunityIndex, 1);
        
        // Mettre à jour le statut et la probabilité automatiquement
        movedOpportunity.statut = newStage;
        const probabilityDefaults = {
          'prospection': 10,
          'qualification': 25,
          'proposition': 50,
          'négociation': 75,
          'gagnée': 100,
          'perdue': 0
        };
        movedOpportunity.probabilite = probabilityDefaults[newStage] || movedOpportunity.probabilite;
        
        // Ajouter à la destination
        destStage.splice(destination.index, 0, movedOpportunity);
        
        setPipelineData(updatedPipelineData);
        
        console.log('Local state updated successfully');
        
        // Rafraîchir les données depuis le serveur après un délai
        setTimeout(() => {
          console.log('Refreshing data from server');
          fetchPipelineData();
        }, 1000);
        
      } else {
        const errorData = await response.text();
        console.error('API Error:', response.status, errorData);
        alert('Erreur lors de la mise à jour du statut');
      }
    } catch (error) {
      console.error('Drag & Drop Error:', error);
      alert('Erreur de connexion lors du changement de statut');
    }
  };

  const handleCardClick = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setShowSidebar(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStageTotal = (stageName) => {
    const opportunities = pipelineData.stages[stageName] || [];
    return opportunities.reduce((sum, opp) => sum + (opp.valeur_estimee || 0), 0);
  };

  if (loading) {
    return (
      <div className="pipeline-loading">
        <div className="text-center">
          <div className="baobab-spinner mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Chargement du pipeline</h3>
          <p className="text-gray-600">Préparation de votre pipeline commercial...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pipeline-error">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Erreur de chargement</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchPipelineData}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pipeline-board">
      <div className="pipeline-header">
        <div className="pipeline-title">
          <h1 className="text-2xl font-bold text-gray-900">Pipeline Commercial</h1>
          <p className="text-gray-600 mt-1">
            {pipelineData.total_opportunities} opportunités au total
          </p>
        </div>
        
        {/* Filtres Avancés */}
        <div className="pipeline-filters">
          <div className="filters-row-1">
            {/* Filtre Commercial */}
            <select
              value={filters.owner}
              onChange={(e) => setFilters({...filters, owner: e.target.value})}
              className="filter-select"
            >
              <option value="">Tous les commerciaux</option>
              {availableCommercials.map(commercial => (
                <option key={commercial.id} value={commercial.full_name}>
                  {commercial.full_name} ({commercial.role})
                </option>
              ))}
            </select>
            
            {/* Filtre Statut */}
            <select
              value={filters.statut}
              onChange={(e) => setFilters({...filters, statut: e.target.value})}
              className="filter-select"
            >
              <option value="">Tous les statuts</option>
              <option value="prospection">Prospection</option>
              <option value="qualification">Qualification</option>
              <option value="proposition">Proposition</option>
              <option value="négociation">Négociation</option>
              <option value="gagnée">Gagnée</option>
              <option value="perdue">Perdue</option>
            </select>
            
            {/* Filtre Société */}
            <input
              type="text"
              placeholder="Filtrer par société"
              value={filters.company}
              onChange={(e) => setFilters({...filters, company: e.target.value})}
              className="filter-input"
              list="companies-list"
            />
            <datalist id="companies-list">
              {availableCompanies.map(company => (
                <option key={company.id} value={company.name} />
              ))}
            </datalist>
          </div>
          
          <div className="filters-row-2">
            {/* Filtres Montant */}
            <input
              type="number"
              placeholder="Montant min (FCFA)"
              value={filters.amountMin}
              onChange={(e) => setFilters({...filters, amountMin: e.target.value})}
              className="filter-input-small"
            />
            
            <input
              type="number"
              placeholder="Montant max (FCFA)"
              value={filters.amountMax}
              onChange={(e) => setFilters({...filters, amountMax: e.target.value})}
              className="filter-input-small"
            />
            
            {/* Filtres Date de Clôture */}
            <input
              type="date"
              value={filters.expectedCloseDateFrom}
              onChange={(e) => setFilters({...filters, expectedCloseDateFrom: e.target.value})}
              className="filter-input-small"
              title="Date de clôture depuis"
            />
            
            <input
              type="date"
              value={filters.expectedCloseDateTo}
              onChange={(e) => setFilters({...filters, expectedCloseDateTo: e.target.value})}
              className="filter-input-small"
              title="Date de clôture jusqu'à"
            />
            
            {/* Bouton Reset Filtres */}
            <button
              onClick={() => setFilters({
                owner: '',
                statut: '',
                amountMin: '',
                amountMax: '',
                expectedCloseDateFrom: '',
                expectedCloseDateTo: '',
                company: ''
              })}
              className="filter-reset-btn"
              title="Réinitialiser les filtres"
            >
              🔄 Reset
            </button>
          </div>
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="pipeline-columns">
          {Object.entries(stageConfig).map(([stageId, config]) => {
            const opportunities = pipelineData.stages[stageId] || [];
            const stageTotal = getStageTotal(stageId);
            
            return (
              <div key={stageId} className="pipeline-column">
                <div className="pipeline-column-header" style={{ borderTopColor: config.color }}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{config.icon}</span>
                    <h3 className="font-semibold text-gray-900">{config.title}</h3>
                    <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-sm">
                      {opportunities.length}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {formatCurrency(stageTotal)}
                  </div>
                </div>

                <Droppable droppableId={stageId}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`pipeline-drop-zone ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                      style={{ backgroundColor: snapshot.isDraggingOver ? config.bgColor : 'transparent' }}
                    >
                      {opportunities.map((opportunity, index) => (
                        <Draggable
                          key={opportunity.id}
                          draggableId={opportunity.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`pipeline-card ${snapshot.isDragging ? 'dragging' : ''}`}
                              onClick={() => handleCardClick(opportunity)}
                            >
                              <div className="pipeline-card-header">
                                <h4 className="font-semibold text-sm text-gray-900 truncate">
                                  {opportunity.nom_opportunite}
                                </h4>
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                  <span>{opportunity.probabilite}%</span>
                                  <div 
                                    className="w-8 h-1 bg-gray-200 rounded-full overflow-hidden"
                                  >
                                    <div 
                                      className="h-full bg-green-500"
                                      style={{ width: `${opportunity.probabilite}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                              
                              <div className="pipeline-card-body">
                                <p className="text-sm text-gray-600 truncate mb-2">
                                  🏢 {opportunity.client_name}
                                </p>
                                
                                {/* Commercial Badge avec Couleur */}
                                <div className="commercial-badge mb-2">
                                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                    👤 {opportunity.commercial_responsable.split('(')[0].trim()}
                                  </span>
                                </div>
                                
                                <div className="flex justify-between items-center">
                                  <span className="font-bold text-sm text-green-600">
                                    {formatCurrency(opportunity.valeur_estimee)}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    #{opportunity.numero_opportunite?.split('-').pop() || 'N/A'}
                                  </span>
                                </div>
                              </div>
                              
                              {opportunity.date_cloture_prevue && (
                                <div className="pipeline-card-footer">
                                  <span className="text-xs text-gray-500">
                                    Échéance: {new Date(opportunity.date_cloture_prevue).toLocaleDateString('fr-FR')}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      
                      {opportunities.length === 0 && (
                        <div className="pipeline-empty-state">
                          <p className="text-gray-500 text-sm text-center py-8">
                            Aucune opportunité
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>

      {/* Sidebar */}
      {showSidebar && selectedOpportunity && (
        <OpportunitySidebar
          opportunity={selectedOpportunity}
          isOpen={showSidebar}
          onClose={() => {
            setShowSidebar(false);
            setSelectedOpportunity(null);
          }}
          onUpdate={(updatedOpportunity) => {
            // Mettre à jour l'opportunité dans le state local
            const updatedPipelineData = { ...pipelineData };
            Object.keys(updatedPipelineData.stages).forEach(stage => {
              const index = updatedPipelineData.stages[stage].findIndex(
                opp => opp.id === updatedOpportunity.id
              );
              if (index !== -1) {
                updatedPipelineData.stages[stage][index] = updatedOpportunity;
              }
            });
            setPipelineData(updatedPipelineData);
            setSelectedOpportunity(updatedOpportunity);
          }}
        />
      )}
    </div>
  );
};

export default PipelineBoard;